import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useToast } from '../contexts/ToastContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import Select from '../components/Select'
import {
  HelpCircle,
  Plus,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  X,
  FileText,
  Search,
} from 'lucide-react'
import { formatDate } from '../utils/helpers'
import { SkeletonContainer, SkeletonListItem } from '../components/SkeletonLoader'

const SUPPORT_TICKET_CATEGORIES = [
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'account', label: 'Account Issue' },
  { value: 'other', label: 'Other' },
]

const TICKET_STATUSES = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
}

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

const Support = () => {
  const organizationId = useOrganizationId()
  const { success, error: showError } = useToast()
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
  })

  useEffect(() => {
    if (organizationId) {
      fetchTickets()
    }
  }, [organizationId])

  const fetchTickets = async () => {
    if (!organizationId) return
    setLoading(true)
    let hasData = false
    try {
      // Try to fetch with user join, but if it fails, fetch without join
      let { data, error } = await supabase
        .from('support_tickets')
        .select('*, users:user_id(email, raw_user_meta_data)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      // If join fails, try without the join
      if (error && (error.code === 'PGRST116' || error.message?.includes('relation') || error.message?.includes('permission'))) {
        const { data: ticketsData, error: ticketsError } = await supabase
          .from('support_tickets')
          .select('*')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false })
        
        if (ticketsError) throw ticketsError
        data = ticketsData
        error = null
      }

      if (error) throw error
      
      if (data && data.length > 0) {
        hasData = true
      }
      
      setTickets(data || [])
    } catch (error) {
      console.error('Error fetching tickets:', error)
      // Only show error if we didn't successfully load any data
      if (!hasData) {
        showError('Unable to load support tickets. Please refresh the page.', {
          title: 'Loading Failed',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTicket = async (e) => {
    e.preventDefault()
    if (!organizationId) return

    try {
      setSubmitting(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          organization_id: organizationId,
          user_id: user.id,
          subject: formData.subject,
          category: formData.category,
          priority: formData.priority,
          description: formData.description,
          status: 'open',
        })
        .select()
        .single()

      if (error) throw error

      // Reset form
      setFormData({
        subject: '',
        category: '',
        priority: 'medium',
        description: '',
      })
      setShowCreateForm(false)
      
      // Reset filters to show the new ticket
      setSearchTerm('')
      setFilterStatus('all')
      
      // Refresh tickets list
      await fetchTickets()
      
      success('Your support ticket has been created successfully. Our team will respond as soon as possible.', {
        title: 'Ticket Created',
        duration: 6000,
      })
    } catch (error) {
      console.error('Error creating ticket:', error)
      showError(error.message || 'Failed to create support ticket. Please try again.', {
        title: 'Creation Failed',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch = searchTerm === '' || 
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const openTicketDetails = (ticket) => {
    setSelectedTicket(ticket)
  }

  const closeTicketDetails = () => {
    setSelectedTicket(null)
  }

  if (loading) {
    return (
      <div className="section-container">
        <SkeletonContainer count={5}>
          <SkeletonListItem />
        </SkeletonContainer>
      </div>
    )
  }

  return (
    <div className="section-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Support Center</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {tickets.length > 0 
              ? `${tickets.length} ticket${tickets.length !== 1 ? 's' : ''} total`
              : 'Get help, report issues, or request features'}
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus size={20} className="mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            type="text"
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'open', label: 'Open' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'closed', label: 'Closed' },
          ]}
          className="w-full sm:w-48"
        />
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 && tickets.length > 0 ? (
        <Card>
          <div className="text-center py-12 px-6">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No tickets match your filters
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Try adjusting your search or filter to see your tickets
            </p>
            <Button 
              variant="secondary"
              onClick={() => {
                setSearchTerm('')
                setFilterStatus('all')
              }}
            >
              Clear Filters
            </Button>
          </div>
        </Card>
      ) : filteredTickets.length === 0 ? (
        <Card>
          <div className="text-center py-12 px-6">
            <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No support tickets yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first support ticket to get help
            </p>
            {!showCreateForm && (
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus size={20} className="mr-2" />
                Create Ticket
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <motion.div
              key={ticket.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card
                variant="soft"
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => openTicketDetails(ticket)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {ticket.subject}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${TICKET_STATUSES[ticket.status]?.color || ''}`}>
                        {TICKET_STATUSES[ticket.status]?.label || ticket.status}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${PRIORITY_COLORS[ticket.priority] || ''}`}>
                        {ticket.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {ticket.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileText size={14} />
                        {ticket.category ? SUPPORT_TICKET_CATEGORIES.find(c => c.value === ticket.category)?.label : 'Uncategorized'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatDate(ticket.created_at)}
                      </span>
                      {ticket.updated_at !== ticket.created_at && (
                        <span className="flex items-center gap-1">
                          Updated {formatDate(ticket.updated_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <MessageSquare className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setShowCreateForm(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <Card variant="glass" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6 p-6 pb-4">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Support Ticket</h2>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleCreateTicket} className="space-y-4 px-6 pb-6" noValidate>
                  <Input
                    label="Subject"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Select
                      label="Category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      options={SUPPORT_TICKET_CATEGORIES}
                      required
                    />

                    <Select
                      label="Priority"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      options={[
                        { value: 'low', label: 'Low' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'high', label: 'High' },
                        { value: 'urgent', label: 'Urgent' },
                      ]}
                      required
                    />
                  </div>

                  <Textarea
                    label="Description"
                    placeholder="Please provide as much detail as possible about your issue, question, or request..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    required
                  />

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={submitting || !formData.subject || !formData.category || !formData.description}
                      className="flex-1"
                    >
                      <Send size={18} className="mr-2" />
                      {submitting ? 'Creating...' : 'Create Ticket'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Ticket Details Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={closeTicketDetails}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <Card variant="glass" className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6 p-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedTicket.subject}
                      </h2>
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${TICKET_STATUSES[selectedTicket.status]?.color || ''}`}>
                        {TICKET_STATUSES[selectedTicket.status]?.label || selectedTicket.status}
                      </span>
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${PRIORITY_COLORS[selectedTicket.priority] || ''}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        Created: {formatDate(selectedTicket.created_at)}
                      </span>
                      {selectedTicket.updated_at !== selectedTicket.created_at && (
                        <span>
                          Updated: {formatDate(selectedTicket.updated_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={closeTicketDetails}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ml-4"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="px-6 pb-6 space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                      Category
                    </h3>
                    <p className="text-gray-900 dark:text-white">
                      {selectedTicket.category ? SUPPORT_TICKET_CATEGORIES.find(c => c.value === selectedTicket.category)?.label : 'Uncategorized'}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                      Description
                    </h3>
                    <div className="prose dark:prose-invert max-w-none">
                      <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                        {selectedTicket.description}
                      </p>
                    </div>
                  </div>

                  {selectedTicket.admin_response && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2">
                        <MessageSquare size={16} />
                        Admin Response
                      </h3>
                      <p className="text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                        {selectedTicket.admin_response}
                      </p>
                      {selectedTicket.admin_response_at && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          Responded: {formatDate(selectedTicket.admin_response_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedTicket.status === 'open' && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">
                            Ticket is open
                          </p>
                          <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-1">
                            Our support team will review your ticket and respond as soon as possible. You will be notified when there's an update.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Support

